/**
 * Столбцы «Аналитики» и факты к ним (макет docs/design/analytics-chart-2026-10-13).
 *
 * Столбец — день или месяц, раскрашенный по категориям: тот же подсчёт, что
 * у списка категорий (`rangeReport`). Возврат уменьшает свою категорию в свой
 * день; поступления — отдельный поток. Переводы и исправления остатка — ни то
 * ни другое.
 *
 * Факты — то, что пояснение под графиком вправе сказать. Числа считаются
 * здесь, слова подбирает модель или шаблон: оба говорят одно и то же, и
 * проверить можно каждое число.
 */
import { add, kop, roundHalfEven, sub, ZERO, type Money } from './money.js';
import { addDays, diffDays, type PlainDate } from './date.js';
import type { AnalyticsTx } from './analytics.js';

/** Деление денег с банковским округлением: копейки не теряются в сторону. */
const divRound = (m: Money, n: number): Money => kop(roundHalfEven(m / n));

export type ChartFlow = 'expense' | 'income';

const isRefund = (t: AnalyticsTx): boolean =>
  t.type === 'income' && t.link?.kind === 'income' && t.link.incomeKind === 'refund';

/** Часть плана траты: обязательный платёж, повседневные нужды или свободные траты. */
export type SpendKind = 'need' | 'want' | 'obligations';

/**
 * Куда трата идёт в плане — тем же правилом, что «Итоги» (`periodFacts`):
 * оплата обязательного платежа — «Обязательные платежи», остальное — по
 * части плана, без неё — нужды. Возврат — в часть своей покупки. Взнос в цель
 * тратой не считается. Не трата — `null`.
 */
export function spendKind(t: AnalyticsTx): SpendKind | null {
  if (t.type === 'expense') {
    if (t.link?.kind === 'obligation') return 'obligations';
    if (t.link?.kind === 'goal') return null;
    return t.part === 'want' ? 'want' : 'need';
  }
  if (isRefund(t)) return t.part === 'want' ? 'want' : 'need';
  return null;
}

/** Категория операции в графике — как в `rangeReport`: без категории — «прочее». */
export const sliceOf = (t: AnalyticsTx, flow: ChartFlow): string =>
  t.categoryId ?? (flow === 'expense' ? 'other' : 'income-other');

/**
 * Что операция даёт графику: трата — плюс, возврат — минус в расходах,
 * поступление — плюс в поступлениях. Остальное — ноль.
 */
export function flowValue(t: AnalyticsTx, flow: ChartFlow): Money {
  if (t.type === 'transfer' || t.type === 'adjustment') return ZERO;
  if (flow === 'expense') return t.type === 'expense' ? t.amount : isRefund(t) ? sub(ZERO, t.amount) : ZERO;
  return t.type === 'income' && !isRefund(t) ? t.amount : ZERO;
}

/** Считается ли операция «покупкой» (или «поступлением») для счётчика. */
export const countsAs = (t: AnalyticsTx, flow: ChartFlow): boolean =>
  flow === 'expense' ? t.type === 'expense' : t.type === 'income' && !isRefund(t);

export interface ChartBucket {
  /** 'YYYY-MM-DD' — день, 'YYYY-MM' — месяц. */
  key: string;
  /** Сумма столбца с возвратами. */
  total: Money;
  /** По категориям: может быть меньше нуля, если возвратов больше трат. */
  bySlice: Record<string, Money>;
  /** Покупок (или поступлений) в столбце. */
  count: number;
}

/**
 * Столбцы с `from` по `to` включительно — каждый день (месяц), даже пустой:
 * пропуск на графике тоже сведения.
 */
export function chartBuckets(input: {
  txs: readonly AnalyticsTx[];
  from: PlainDate;
  to: PlainDate;
  flow: ChartFlow;
  byMonth: boolean;
  /** Какие категории показывать: выбранная — с подкатегориями. */
  inside?: (categoryId: string) => boolean;
}): ChartBucket[] {
  const keyOf = (d: string) => (input.byMonth ? d.slice(0, 7) : d);
  const out: ChartBucket[] = [];
  const at = new Map<string, ChartBucket>();
  for (let d = input.from; d <= input.to; d = addDays(d, 1)) {
    const k = keyOf(d);
    if (!at.has(k)) { const b = { key: k, total: ZERO, bySlice: {}, count: 0 }; at.set(k, b); out.push(b); }
  }
  for (const t of input.txs) {
    if (t.occurredDate < input.from || t.occurredDate > input.to) continue;
    const v = flowValue(t, input.flow);
    if (v === ZERO) continue;
    const id = sliceOf(t, input.flow);
    if (input.inside && !input.inside(id)) continue;
    const b = at.get(keyOf(t.occurredDate));
    if (!b) continue;
    b.total = add(b.total, v);
    b.bySlice[id] = add(b.bySlice[id] ?? ZERO, v);
    if (countsAs(t, input.flow)) b.count += 1;
  }
  return out;
}

/**
 * Средний столбец: итог, поделённый на число дней (месяцев) — включая пустые.
 * Пустой день — тоже день периода: без него средняя завышалась бы ровно
 * у того, кто тратит не каждый день.
 */
export const chartAverage = (buckets: readonly ChartBucket[]): Money =>
  buckets.length === 0 ? ZERO : divRound(add(...buckets.map((b) => b.total)), buckets.length);

/** Сумма до целого рубля. */
export const toRubles = (m: Money): Money => kop(divRound(m, 100) * 100);

/** Сумма, округлённая до сотни рублей, — для «около 1 800 ₽». */
const roughly = (m: Money): Money => kop(divRound(m, 10_000) * 10_000);

export interface ChartFacts {
  flow: ChartFlow;
  unit: 'day' | 'month';
  from: PlainDate;
  to: PlainDate;
  /** Итог за период — тот же, что над графиком. */
  total: Money;
  /** Покупок (поступлений). */
  count: number;
  /** Сколько столбцов. */
  buckets: number;
  /** В среднем за столбец. */
  average: Money;
  /** До трёх крупнейших категорий. */
  top: { name: string; sum: Money; count: number; percent: number }[];
  /**
   * Самый большой столбец, если он заметно выше среднего, — и средняя без
   * него, округлённая до сотни: «без него выходит около 1 800 ₽ в день».
   */
  peak?: { key: string; sum: Money; name: string; nameSum: Money; restAverage: Money };
  /** Свободные траты против обычной суммы в месяц — только у идущего периода по всем счетам. */
  want?: { spent: Money; usual: Money; daysPassed: number; daysTotal: number };
}

/**
 * Что можно сказать о периоде.
 *
 * `slices` — категории по убыванию суммы, как в списке под графиком;
 * `nameOf` даёт имя. Пусто — `null`: пояснять нечего.
 */
export function chartFacts(input: {
  flow: ChartFlow;
  from: PlainDate;
  to: PlainDate;
  buckets: readonly ChartBucket[];
  byMonth: boolean;
  slices: readonly { categoryId: string; sum: Money; percent: number; count: number }[];
  nameOf: (categoryId: string) => string;
  want?: ChartFacts['want'];
}): ChartFacts | null {
  const total = add(...input.buckets.map((b) => b.total));
  if (total <= ZERO || input.buckets.length === 0) return null;
  const count = input.buckets.reduce((a, b) => a + b.count, 0);
  // До рубля: «2 068 ₽ в день», а не «2 067,79 ₽».
  const average = toRubles(chartAverage(input.buckets));
  const top = input.slices.filter((s) => s.sum > ZERO).slice(0, 3)
    .map((s) => ({ name: input.nameOf(s.categoryId), sum: s.sum, count: s.count, percent: s.percent }));

  let peak: ChartFacts['peak'];
  const biggest = [...input.buckets].sort((a, b) => b.total - a.total || a.key.localeCompare(b.key))[0];
  // «Самый дорогой день» — только когда он выделяется: вдвое выше среднего
  // и столбцов хотя бы четыре. Иначе это просто один из обычных дней.
  if (biggest && input.buckets.length >= 4 && biggest.total >= chartAverage(input.buckets) * 2) {
    const main = Object.entries(biggest.bySlice).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
    if (main && main[1] > ZERO) {
      const rest = divRound(sub(total, biggest.total), input.buckets.length - 1);
      peak = { key: biggest.key, sum: biggest.total, name: input.nameOf(main[0]), nameSum: main[1], restAverage: roughly(rest) };
    }
  }
  return {
    flow: input.flow, unit: input.byMonth ? 'month' : 'day', from: input.from, to: input.to,
    total, count, buckets: input.buckets.length, average, top,
    ...(peak ? { peak } : {}),
    ...(input.want && input.want.usual > ZERO ? { want: input.want } : {}),
  };
}

/** Сколько дней в отрезке, включая оба края. */
export const daysIn = (from: PlainDate, to: PlainDate): number => diffDays(from, to) + 1;
